import { API_HEADERS } from "@api/utils";
import { gql } from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { useDescope } from "@descope/react-sdk";
import {
  useLocalStorage,
  NUMBER_SERIALIZATION,
  STRING_SERIALIZATION,
} from "@scribenote/utils";
import { captureException } from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useContext, createContext, useEffect } from "react";
import { useNavigate } from "react-router";
import { useIntercom } from "react-use-intercom";

import { useMomentNow } from "./use-moment";
import { useWS } from "./use-ws";
import { defaultErrorHandler } from "../utils/operationTracking";

const MS_IN_HOUR = 3600000;
const MS_IN_DAY = 86400000;

const LOGIN_USER_RESPONSE_FIELDS = gql`
  fragment LoginUserResponseFields on LoginUserResponse {
    uuid
  }
`;

const LOGIN_USER = gql`
  ${LOGIN_USER_RESPONSE_FIELDS}

  mutation LoginUser($email: String, $password: String) {
    loginUser(email: $email, password: $password) {
      user {
        ...LoginUserResponseFields
      }
      accessCsrf
    }
  }
`;

const CREATE_USER = gql`
  ${LOGIN_USER_RESPONSE_FIELDS}

  mutation CreateUser(
    $firstName: String
    $lastName: String
    $email: String
    $password: String
    $createdAt: DateTime
    $referralCode: String
    $isOnboardingComplete: Boolean
    $role: Int
  ) {
    createUser(
      firstName: $firstName
      lastName: $lastName
      email: $email
      password: $password
      createdAt: $createdAt
      referralCode: $referralCode
      isOnboardingComplete: $isOnboardingComplete
      role: $role
    ) {
      user {
        ...LoginUserResponseFields
      }
      accessCsrf
      hasReferrer
    }
  }
`;

const CREATE_USER_FROM_TEAM_INVITATION = gql`
  ${LOGIN_USER_RESPONSE_FIELDS}

  mutation createUserFromTeamInvitation(
    $firstName: String
    $lastName: String
    $email: String
    $password: String
    $createdAt: DateTime
    $accessToken: String
  ) {
    createUserFromTeamInvitation(
      firstName: $firstName
      lastName: $lastName
      email: $email
      password: $password
      createdAt: $createdAt
      accessToken: $accessToken
    ) {
      user {
        ...LoginUserResponseFields
      }
      accessCsrf
    }
  }
`;

const PERSISTENT_LOGIN_USER = gql`
  ${LOGIN_USER_RESPONSE_FIELDS}

  mutation PersistentLoginUser {
    persistentLoginUser {
      user {
        ...LoginUserResponseFields
      }
      accessCsrf
    }
  }
`;

const LOGOUT_USER = gql`
  mutation LogoutUser {
    logoutUser {
      ok
    }
  }
`;

const EXCHANGE_DESCOPE_TOKEN = gql`
  ${LOGIN_USER_RESPONSE_FIELDS}

  mutation ExchangeDescopeToken {
    descopeWebSession {
      user {
        ...LoginUserResponseFields
      }
      accessCsrf
    }
  }
`;

const authContext = createContext({
  userUuid: null,
  isUserAuthenticated: false,
  error: null,
  loading: false,
  signin: () => {},
  reauthenticateActiveUser: () => {},
  register: () => {},
  registerAsTeamMember: () => {},
  logout: () => {},
  isTokenExpired: false,
  isTokenExpiringSoon: false,

  // Descope
  descopeSignin: () => {},
  exchangeDescopeAuthCode: () => {},
});

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = () => {
  return useContext(authContext);
};

// Provider hook that creates auth object and handles state
export function useProvideAuth() {
  const [userUuid, setUserUuid] = useLocalStorage(
    "user_uuid",
    null,
    STRING_SERIALIZATION,
  );
  const [authExpiry, setAuthExpiry] = useLocalStorage(
    "auth_expiry",
    null,
    NUMBER_SERIALIZATION,
  );
  const [accessCsrf, setAccessCsrf] = useLocalStorage(
    "accessCsrf",
    null,
    STRING_SERIALIZATION,
  );
  const { shutdown } = useIntercom();
  const { earlyTokenExpiration } = useFlags();

  const descope = useDescope();

  const momentNow = useMomentNow(
    earlyTokenExpiration ? 5_000 : 30_000,
  );

  // Token expiry
  const authExpiryDelay = earlyTokenExpiration ? 30_000 : MS_IN_DAY;
  const authAlmostExpiryWindow = earlyTokenExpiration
    ? 15_000
    : MS_IN_HOUR * 12;
  const isTokenExpired = authExpiry ? momentNow > authExpiry : false;
  const isTokenExpiringSoon = authExpiry
    ? momentNow > authExpiry - authAlmostExpiryWindow
    : false;

  const isUserAuthenticated =
    userUuid && accessCsrf && !isTokenExpired;

  // Keep API headers in sync with the CSRF
  useEffect(() => {
    API_HEADERS.accessCsrf = accessCsrf;
  }, [accessCsrf]);

  const [loginUser, { error: loginError, loading: loginLoading }] =
    useMutation(LOGIN_USER);

  const [persistentLoginUser, { error: persistentLoginError }] =
    useMutation(PERSISTENT_LOGIN_USER);

  const [exchangeDescopeToken] = useMutation(EXCHANGE_DESCOPE_TOKEN);

  const [
    createUser,
    { error: createUserError, loading: createUserLoading },
  ] = useMutation(CREATE_USER);

  const [
    createUserFromTeamInvitation,
    {
      error: createUserFromTeamInvitationError,
      loading: createUserFromTeamInvitationLoading,
    },
  ] = useMutation(CREATE_USER_FROM_TEAM_INVITATION);

  const [
    logoutUser,
    {
      error: logoutError,
      client: errorClientLogout,
      loading: logoutLoading,
    },
  ] = useMutation(LOGOUT_USER);

  const navigate = useNavigate();
  const { disconnectAll: disconnectAllWebSockets } = useWS();

  const setAuthWithMutationResults = (mutationResults) => {
    const is_user_authenticated = mutationResults;
    const {
      accessCsrf,
      user: { uuid },
    } = mutationResults;
    if (is_user_authenticated && accessCsrf) {
      setUserUuid(uuid);
      setAccessCsrf(accessCsrf);

      setAuthExpiry(moment.now() + authExpiryDelay);
      localStorage.removeItem("already_reloaded"); // on sign in, remove the reload flag so kickEmOut will work again
    }

    // if (!mutationResults?.user?.isConfirmed) {
    //   navigate("/registration_confirmation");
    // } REMOVE EMAIL CONFIRMATION
  };

  const descopeSignin = async (email) => {
    const res = await descope.saml.start(
      email,
      `${window.location.origin}/sso/login/descope`,
    );
    if (res.error) {
      alert("error", "Failed to initialize authentication flow");
      throw new Error(
        `Descope SAML failed: ${res.error.errorCode} ${res.error.errorDescription} ${res.error.errorMessage}`,
      );
    }

    // Open the Descope SSO URL, which will redirect back to /sso/login/descope?code=<descope_auth_code>
    location = res.data.url;
  };

  const exchangeDescopeAuthCode = async (descopeCode) => {
    const res = await descope.oauth.exchange(descopeCode);
    if (res.error) {
      throw new Error(
        `Descope OAuth token exchange failed: ${res.error.errorCode} ${res.error.errorDescription} ${res.error.errorMessage}`,
      );
    }

    const exchangeResponse = await exchangeDescopeToken({
      context: {
        headers: { Authorization: `Bearer ${res.data.sessionJwt}` },
      },
    });
    setAuthWithMutationResults(
      exchangeResponse.data.descopeWebSession,
    );
  };

  const signin = async (email, password) => {
    try {
      const response = await loginUser({
        variables: { email, password },
      });

      mixpanel.track("Sign In");

      setAuthWithMutationResults(response.data.loginUser);
    } catch (errResponse) {
      if (!CombinedGraphQLErrors.is(errResponse)) {
        captureException(errResponse);
      }

      errResponse?.errors?.map((error) => {
        alert("error", error.message);
        if (
          error.message ===
          "Invalid authentication credentials supplied. Please try logging in again."
        ) {
          mixpanel.track("Sign In Error", {
            error: error.message,
            email: email,
          });
        }
      });
    }
  };

  const reauthenticateActiveUser = async () => {
    try {
      const response = await persistentLoginUser();

      mixpanel.track("Reauthenticated Active User");

      const mutationResults = response.data.persistentLoginUser;

      const is_user_authenticated = mutationResults;

      const {
        accessCsrf,
        user: { uuid },
      } = mutationResults;

      if (is_user_authenticated && accessCsrf) {
        setUserUuid(uuid);
        setAccessCsrf(accessCsrf);
        setAuthExpiry(moment.now() + authExpiryDelay);
      }
    } catch (errResponse) {
      defaultErrorHandler(errResponse);
    }
  };

  const postRegistrationCommon = (
    email,
    referralCode,
    clinicName,
    contactType,
    contactInfo,
    contactInfoLocation,
    userUuid,
  ) => {
    mixpanel.track("Sign Up", {
      referralCode,
      clinicName,
      contactType,
      contactInfo,
      contactInfoLocation,
    });

    // Set user_id synchronously before firing the event so it's
    // included in the sign_up_complete hit (the useEffect in
    // CurrentUserProvider won't have run yet due to React 19 batching).
    window.gtag("set", { user_id: userUuid });
    window.gtag("event", "sign_up_complete");

    window.fbq("track", "CompleteRegistration", { email });
  };

  const register = (
    email,
    password,
    firstName,
    lastName,
    referralCode,
    isOnboardingComplete = false,
    clinicName,
    contactType,
    contactInfo,
    contactInfoLocation,
    role,
  ) => {
    return createUser({
      variables: {
        email,
        password,
        firstName,
        lastName,
        createdAt: moment().format(),
        referralCode,
        isOnboardingComplete,
        role,
      },
    })
      .then((response) => {
        setAuthWithMutationResults(response.data.createUser);

        postRegistrationCommon(
          email,
          referralCode,
          clinicName,
          contactType,
          contactInfo,
          contactInfoLocation,
          response.data.createUser.user.uuid,
        );

        // Return hasReferrer so caller can show appropriate message
        return { hasReferrer: response.data.createUser.hasReferrer };
      })
      .catch((error) => {
        mixpanel.track("Sign Up Error", {
          error: error.message,
          email: email,
        });
        throw error;
      });
  };

  const registerAsTeamMember = ({
    email,
    password,
    firstName,
    lastName,
    accessToken,
    clinicName,
    contactType,
    contactInfo,
    contactInfoLocation,
  }) => {
    return createUserFromTeamInvitation({
      variables: {
        email: email,
        password,
        firstName: firstName,
        lastName: lastName,
        createdAt: moment().format(),
        accessToken,
      },
    })
      .then((response) => {
        setAuthWithMutationResults(
          response.data.createUserFromTeamInvitation,
        );

        // Team members are exempt from the referral program, so pass null for referralCode
        postRegistrationCommon(
          email,
          null,
          clinicName,
          contactType,
          contactInfo,
          contactInfoLocation,
          response.data.createUserFromTeamInvitation.user.uuid,
        );
      })
      .catch((error) => {
        mixpanel.track("Sign Up Error", {
          error: error.message,
          email: email,
        });
        throw error;
      });
  };

  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      mixpanel.track("Sign Out Error", {
        error,
      });
    }

    disconnectAllWebSockets();
    setAccessCsrf(null);
    setAuthExpiry(null);
    setUserUuid(null);
    localStorage.removeItem("selected-date-range");
    localStorage.removeItem("already_reloaded"); //on log out, remove the reload flag so kickEmOut will work again
    localStorage.removeItem("onboardingSkipped");

    shutdown();

    await errorClientLogout.clearStore();
    let currentTimeInHours = new Date().getHours();

    const isDay = currentTimeInHours >= 3 && currentTimeInHours <= 16;

    const message = isDay
      ? "You've been signed out. We hope you have a wonderful day 🤙"
      : "You've been signed out. We hope you have a wonderful night 🤙";
    alert("info", message);
    navigate("/auth/login");
  };

  return {
    userUuid,
    isUserAuthenticated,
    error:
      loginError ||
      persistentLoginError ||
      createUserError ||
      createUserFromTeamInvitationError ||
      logoutError,
    loading:
      loginLoading ||
      createUserLoading ||
      createUserFromTeamInvitationLoading ||
      logoutLoading,
    signin,
    reauthenticateActiveUser,
    register,
    registerAsTeamMember,
    logout,
    isTokenExpired,
    isTokenExpiringSoon,

    // Descope
    descopeSignin,
    exchangeDescopeAuthCode,
  };
}

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function AuthProvider({ children }) {
  const auth = useProvideAuth();

  return (
    <authContext.Provider value={auth}>
      {children}
    </authContext.Provider>
  );
}
